import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FaCheck,
  FaSync,
  FaBox,
  FaTruck,
  FaDownload,
  FaShoppingBag,
  FaClock,
  FaUserClock,
  FaTimes,
  FaHourglassHalf,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowRight,
} from 'react-icons/fa';
import Loader from '../../Components/utils/Loader';

const getSellerPrimaryCtaLabel = (nextStatus, isDelivery) => {
  const d = {
    'In Progress': 'Start processing this order',
    'Out for Delivery': 'Ship order — add tracking details',
    'Delivered': 'Mark as delivered',
    'Completed': 'About marking completed',
  };
  const p = {
    'Preparing Order': 'Start preparing this order',
    'Ready for Pickup': 'Mark ready for pickup',
    'Picked Up': 'Mark as picked up',
    'Completed': 'About marking completed',
  };
  const map = isDelivery ? d : p;
  return map[nextStatus] || `Continue to: ${nextStatus}`;
};

const OrderTracking = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const routeIsSeller = location.pathname.startsWith('/sellerOrderTrackingPage');
  const isSeller = routeIsSeller || Boolean(location.state?.isSeller);

  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [deliveryPartner, setDeliveryPartner] = useState('');
  const [trackingId, setTrackingId] = useState('');
  const [trackingLink, setTrackingLink] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('');
  const [timelineStatus, setTimelineStatus] = useState('');
  const [modal, setModal] = useState({ open: false, type: 'message', message: '', onConfirm: null });

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrder(response.data.order);
      } catch (error) {
        console.error('Error fetching order:', error);
        setOrder(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      const mappedStatusHistory = order.statusHistory
        .filter(status => status.name !== 'Past Due')
        .map(status => {
          let description = '';
          if (status.name === 'OnHold' && status.data.type === 'cancellation_request') {
            description = `${status.data.requestedBy.charAt(0).toUpperCase() + status.data.requestedBy.slice(1)} has requested to cancel the order. Reason: ${status.data.reason}`;
          } else if (status.name === 'Cancelled' && status.data.type === 'cancellation_accepted') {
            description = `Cancellation request accepted by ${status.data.acceptedBy}`;
          } else if ((status.name === 'Cancellation Request Rejected' || status.name === 'Preparing Order') && status.data.type === 'cancellation_rejected') {
            description = `The order cancellation request has been rejected by ${status.data.rejectedBy}`;
          } else {
            description = status.data?.note || getDefaultDescription(status.name);
          }
          return {
            status: status.name,
            timestamp: new Date(status.createdAt).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            description,
            deliveryPartner: status.data?.deliveryPartner,
            trackingId: status.data?.trackingId,
            trackingLink: status.data?.trackingLink,
            cancellationRequestedBy: status.data?.requestedBy,
          };
        });
      setStatusHistory(mappedStatusHistory);
      setCurrentStatus(order.currentStatus);
      setTimelineStatus(order.timelineStatus);
    }
  }, [order]);

  if (isLoading) {
    return (
      <div className="trackingDetailsPage">
        <section className="mainSection">
          <Loader type="simple" />
        </section>
      </div>
    );
  }

  if (!order) {
    return <div>Order not found</div>;
  }

  const delivery = location.state?.delivery !== undefined ? location.state.delivery : order.deliveryType === 'delivery';

  const getDefaultDescription = (status) => {
    const descriptions = {
      'Order Placed': 'Your order has been placed successfully.',
      'In Progress': 'Your order is being processed.',
      'Out for Delivery': 'Your order is out for delivery and will arrive soon.',
      'Delivered': 'Your order has been delivered.',
      'Completed': 'Your order has been completed.',
      'Preparing Order': 'Your order is being prepared.',
      'Ready for Pickup': 'Your order is ready for pickup.',
      'Picked Up': 'Your order has been picked up.',
      'OnHold': 'Your order is on hold.',
      'InDispute': 'Your order is in dispute.',
      'Resolved': 'Your order has been resolved.',
      'Delivery Rejected': 'Buyer rejected the delivery.',
      'Pickup Rejected': 'Buyer rejected the pickup.',
    };
    return descriptions[status] || '';
  };

  const deliveryStatusSequence = ['Order Placed', 'In Progress', 'Out for Delivery / Shipped', 'Delivered', 'Completed'];
  const pickupStatusSequence = ['Order Placed', 'Preparing Order', 'Ready for Pickup', 'Picked Up', 'Completed'];
  const specialStatuses = ['OnHold', 'InDispute', 'Resolved'];

  const statusSequence = delivery ? deliveryStatusSequence : pickupStatusSequence;
  let finalStatusSequence = statusSequence;
  let currentIndex = statusSequence.indexOf(timelineStatus);

  if (timelineStatus === 'OnHold') {
    const baseIndex = delivery ? statusSequence.indexOf('In Progress') : statusSequence.indexOf('Preparing Order');
    finalStatusSequence = [...statusSequence.slice(0, baseIndex + 1), 'OnHold', 'Cancelled'];
    currentIndex = finalStatusSequence.indexOf('OnHold');
  } else if (timelineStatus === 'Cancelled') {
    const baseIndex = delivery ? statusSequence.indexOf('In Progress') : statusSequence.indexOf('Preparing Order');
    finalStatusSequence = [...statusSequence.slice(0, baseIndex + 1), 'OnHold', 'Cancelled'];
    currentIndex = finalStatusSequence.indexOf('Cancelled');
  } else if (specialStatuses.includes(timelineStatus)) {
    finalStatusSequence = [...statusSequence.slice(0, currentIndex), timelineStatus];
  }

  const statusIcons = {
    'Order Placed': <FaCheck />,
    'In Progress': <FaSync />,
    'Out for Delivery': <FaTruck />,
    'Delivered': <FaDownload />,
    'Completed': <FaCheckCircle />,
    'Preparing Order': <FaBox />,
    'Ready for Pickup': <FaClock />,
    'Picked Up': <FaUserClock />,
    'OnHold': <FaHourglassHalf />,
    'InDispute': <FaExclamationTriangle />,
    'Resolved': <FaCheckCircle />,
    'Cancelled': <FaTimes />,
  };

  const getStatusClass = (status, index) => {
    if (index < currentIndex || (status === 'Order Placed' && currentIndex >= 0)) return 'completed';
    if (index === currentIndex) return 'active';
    return 'upcoming';
  };

  const handleStatusUpdate = (status) => {
    const nextStatus = statusSequence[currentIndex + 1];
    if (isSeller && status === nextStatus) {
      if (order.orderType === 'deposit' && order.payment.remainingPayment.status !== 'succeeded' && (status === 'Delivered' || status === 'Picked Up')) {
        setModalMessage(`You cannot update the status to ${status} until the buyer pays the remaining balance.`);
        setModalOpen(true);
        return;
      }
      if (status === 'Completed' && (timelineStatus === 'Delivered' || timelineStatus === 'Picked Up')) {
        setModalMessage('The order will be marked as Completed when the buyer confirms delivery or pickup, or automatically after 3 days if the buyer does not respond.');
        setModalOpen(true);
      } else {
        setSelectedStatus(status);
        setStatusUpdateOpen(true);
      }
    } else {
      setModalMessage("You can only update to the next status in the sequence.");
      setModalOpen(true);
    }
  };

  const submitStatusUpdate = async () => {
    if (delivery && selectedStatus === 'Out for Delivery' && (!deliveryPartner || !trackingId || !trackingLink)) {
      setModalMessage("Please provide all shipping details.");
      setModalOpen(true);
      return;
    }

    setIsUpdating(true);
    const timestamp = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const data = {};
    if (delivery && selectedStatus === 'Out for Delivery') {
      data.deliveryPartner = deliveryPartner;
      data.trackingId = trackingId;
      data.trackingLink = trackingLink;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/update-status`, {
        orderId,
        newStatus: selectedStatus,
        ...data,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        if (response.data.order) {
          setOrder(response.data.order);
        } else {
          const newStatusUpdate = {
            status: selectedStatus,
            timestamp,
            description: getDefaultDescription(selectedStatus),
            deliveryPartner: data.deliveryPartner,
            trackingId: data.trackingId,
            trackingLink: data.trackingLink,
          };
          setStatusHistory([newStatusUpdate, ...statusHistory]);
          setTimelineStatus(selectedStatus);
          setCurrentStatus(selectedStatus);
        }
      } else {
        setModalMessage('Failed to update status');
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setModalMessage('An error occurred while updating the status');
      setModalOpen(true);
    } finally {
      setIsUpdating(false);
      setStatusUpdateOpen(false);
      setDeliveryPartner('');
      setTrackingId('');
      setTrackingLink('');
    }
  };

  const handleAccept = async () => {
    if (order.orderType === 'deposit' && order.payment.remainingPayment.status !== 'succeeded') {
      setModalMessage('You cannot accept the order until you pay the remaining balance.');
      setModalOpen(true);
      return;
    }
    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/accept-order`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const newStatusUpdate = {
          status: 'Completed',
          timestamp: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          description: 'Order completed successfully.',
        };
        setStatusHistory([newStatusUpdate, ...statusHistory]);
        setTimelineStatus('Completed');
        setCurrentStatus('Completed');
      } else {
        setModalMessage('Failed to accept order');
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      setModalMessage('An error occurred while accepting the order');
      setModalOpen(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = async () => {
    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/reject-order`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const rejectionStatus = delivery ? 'Delivery Rejected' : 'Pickup Rejected';
        const newTimelineStatus = delivery ? 'In Progress' : 'Preparing Order';
        const newStatusUpdate = {
          status: rejectionStatus,
          timestamp: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          description: 'Buyer rejected the delivery/pickup.',
        };
        setStatusHistory([newStatusUpdate, ...statusHistory]);
        setTimelineStatus(newTimelineStatus);
        setCurrentStatus('Active');
      } else {
        setModalMessage('Failed to reject order');
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error rejecting order:', error);
      setModalMessage('An error occurred while rejecting the order');
      setModalOpen(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const isNonRefundable = order?.orderType === 'deposit' && order?.refundPolicy === 'non_refundable';
  const handleAcceptCancellation = () => {
    const confirmMessage = isNonRefundable
      ? 'This is a non-refundable deposit order. Accepting will cancel the order and the buyer will not receive any refund. Are you sure you want to accept the cancellation request?'
      : 'Are you sure you want to accept the cancellation request? The refund amount will be added to the buyer\'s Chocowi account for future use or payout.';
    setModal({
      open: true,
      type: 'confirm',
      message: confirmMessage,
      onConfirm: async () => {
        setIsUpdating(true);
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/accept-cancellation`,
            { orderId: order._id, acceptedBy: isSeller ? 'seller' : 'buyer' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setModal({
            open: true,
            type: 'message',
            message: 'Cancellation request accepted. Please refresh the page to see the updated status.',
            onConfirm: null,
          });
          const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrder(response.data.order);
        } catch (error) {
          console.error('Error accepting cancellation:', error);
          setModal({
            open: true,
            type: 'message',
            message: 'An error occurred while accepting the cancellation',
            onConfirm: null,
          });
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const handleRejectCancellation = () => {
    setModal({
      open: true,
      type: 'confirm',
      message: 'Are you sure you want to reject the cancellation request?',
      onConfirm: async () => {
        setIsUpdating(true);
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/reject-cancellation`,
            { orderId: order._id, rejectedBy: isSeller ? 'seller' : 'buyer' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setModal({
            open: true,
            type: 'message',
            message: 'Cancellation request rejected. Please refresh the page to see the updated status.',
            onConfirm: null,
          });
          const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrder(response.data.order);
        } catch (error) {
          console.error('Error rejecting cancellation:', error);
          setModal({
            open: true,
            type: 'message',
            message: 'An error occurred while rejecting the cancellation',
            onConfirm: null,
          });
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const shippedStatus = statusHistory.find(item => item.status === 'Out for Delivery');

  const advanceTarget =
    currentIndex >= 0 && currentIndex < statusSequence.length - 1 ? statusSequence[currentIndex + 1] : null;
  const sellerAdvanceTarget =
    isSeller && currentStatus !== 'Cancelled' && advanceTarget && finalStatusSequence.includes(advanceTarget)
      ? advanceTarget
      : null;
  const sellerAwaitingBuyerConfirm =
    isSeller && (timelineStatus === 'Delivered' || timelineStatus === 'Picked Up');
  const showSellerPrimaryAdvance =
    sellerAdvanceTarget &&
    !(sellerAwaitingBuyerConfirm && sellerAdvanceTarget === 'Completed');

  return (
    <div className="trackingDetailsPage">
      <header className="trackingPageHeader">
        <h1 className="pageMainHeading">{isSeller ? 'Update order status' : 'Track your order'}</h1>
      </header>
      <section className="mainSection">
        <div className="trackingDetails">
          <div className="deliveryInfo">
            <div className="packageIcon">
              {delivery ? <FaTruck /> : <FaShoppingBag />}
            </div>
            <div className="deliveryText">
              <div className="deliveryRow">
                <span>{delivery ? 'Delivery Info' : 'Pickup Location'}</span>
                <span>{delivery ? 'Tracking Number' : 'Order Number'}</span>
              </div>
              <div className="deliveryRow">
                <span>
                  {delivery
                    ? `Delivery Partner: ${shippedStatus?.deliveryPartner || 'N/A'}`
                    : `${order?.sellerId?.sellerAddress ? `${order.sellerId.sellerAddress.address}, ${order.sellerId.sellerAddress.city}, ${order.sellerId.sellerAddress.state.name}, ${order.sellerId.sellerAddress.country.name}` : 'N/A'}`
                  }
                </span>
                <span>{delivery ? (shippedStatus?.trackingId || 'N/A') : order.customOrderId}</span>
              </div>
              {delivery && shippedStatus?.trackingLink && (
                <div className="deliveryRow">
                  <span>Tracking Link: <a href={shippedStatus.trackingLink} target="_blank" rel="noopener noreferrer">{shippedStatus.trackingLink}</a></span>
                </div>
              )}
            </div>
          </div>
          {isSeller && sellerAwaitingBuyerConfirm && (
            <div className="sellerBuyerWaitPanel" role="status">
              <FaHourglassHalf className="sellerBuyerWaitPanelIcon" aria-hidden />
              <div>
                <p className="sellerBuyerWaitPanelTitle">Waiting for the buyer</p>
                <p className="sellerBuyerWaitPanelText">
                  You have marked this order as {timelineStatus === 'Delivered' ? 'delivered' : 'picked up'}. The buyer needs
                  to confirm, or the order will complete automatically after a few days if they do not respond. You do not
                  need to tap the final step — completion is handled when the buyer confirms.
                </p>
              </div>
            </div>
          )}
          {isSeller && showSellerPrimaryAdvance && (
            <div className="sellerStatusActionCard">
              <div className="sellerStatusActionCardText">
                <p className="sellerStatusActionLabel">Next step</p>
                <p className="sellerStatusActionTitle">{sellerAdvanceTarget}</p>
                <p className="sellerStatusActionHint">
                  {delivery && sellerAdvanceTarget === 'Out for Delivery'
                    ? 'You will be asked for courier name, tracking ID, and a tracking link.'
                    : 'Confirm below — you can always review the timeline underneath.'}
                </p>
              </div>
              <button
                type="button"
                className="sellerStatusActionPrimaryBtn"
                onClick={() => handleStatusUpdate(sellerAdvanceTarget)}
              >
                <span>{getSellerPrimaryCtaLabel(sellerAdvanceTarget, delivery)}</span>
                <FaArrowRight aria-hidden />
              </button>
            </div>
          )}
          {isSeller && !showSellerPrimaryAdvance && !sellerAwaitingBuyerConfirm && sellerAdvanceTarget === null && currentStatus !== 'Cancelled' && (
            <div className="sellerOrderStatusNote sellerOrderStatusNote--neutral">
              <FaCheckCircle className="orderStatusIcon" />
              <p className="orderStatusText">No further status updates are needed from you on this order.</p>
            </div>
          )}
          <div className="orderProgressSection">
            <h2 className="orderProgressSectionTitle">Order progress</h2>
            <p className="orderProgressSectionHint">
              {isSeller
                ? 'Past stages are greyed out. The next stage you can set is highlighted — you can also tap it on the row below.'
                : 'Each stage updates when the seller moves your order forward.'}
            </p>
          </div>
          <div className="statusTracker" aria-label="Order status timeline">
            {finalStatusSequence.map((statusName, index) => {
              const statusClass = getStatusClass(statusName, index);
              const isClickable = isSeller && statusName === statusSequence[currentIndex + 1] && currentStatus !== 'Cancelled';
              return (
                <div
                  key={statusName}
                  className={`statusStep ${statusClass} ${isClickable ? 'clickable statusStep--next' : ''}`}
                  onClick={() => isClickable && handleStatusUpdate(statusName)}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleStatusUpdate(statusName);
                    }
                  }}
                  aria-label={isClickable ? `Update order to ${statusName}` : undefined}
                >
                  <div className="statusIcon">{statusIcons[statusName] || <FaCheck />}</div>
                  <p>{statusName}</p>
                </div>
              );
            })}
          </div>
          {statusUpdateOpen && (
            <>
              <div className="modalOverlay" onClick={() => setStatusUpdateOpen(false)} />
              <div className="statusUpdateModal">
                <div className="modalHeader">
                  <h3>{selectedStatus === 'Out for Delivery' && delivery ? 'Update Status to Out for Delivery' : 'Confirm Status Update'}</h3>
                  <button className="modalCloseButton" onClick={() => setStatusUpdateOpen(false)}>
                    <FaTimes />
                  </button>
                </div>
                {delivery && selectedStatus === 'Out for Delivery' ? (
                  <>
                    <input
                      type="text"
                      placeholder="Delivery Partner"
                      value={deliveryPartner}
                      onChange={(e) => setDeliveryPartner(e.target.value)}
                      className="modalInput"
                    />
                    <input
                      type="text"
                      placeholder="Tracking ID"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      className="modalInput"
                    />
                    <input
                      type="text"
                      placeholder="Tracking Link"
                      value={trackingLink}
                      onChange={(e) => setTrackingLink(e.target.value)}
                      className="modalInput"
                    />
                  </>
                ) : (
                  <p>
                    Are you sure you want to update the status to {selectedStatus}?
                    {selectedStatus === 'Delivered' || selectedStatus === 'Picked Up'
                      ? ' The order will be marked as ' + selectedStatus + '. It will become Completed when the buyer confirms, or automatically after 3 days if there is no response.'
                      : ''}
                  </p>
                )}
                <div className="modalActions">
                  <button className="modalButton cancel" onClick={() => setStatusUpdateOpen(false)}>
                    Cancel
                  </button>
                  <button className="modalButton submit" onClick={submitStatusUpdate} disabled={isUpdating}>
                    {isUpdating ? <Loader type="miniPlus" /> : 'Confirm'}
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="statusHistorySectionHeader">
            <h2 className="orderProgressSectionTitle">Activity log</h2>
            <p className="orderProgressSectionHint">Newest entries first — includes notes and tracking details when provided.</p>
          </div>
          <div className="statusHistory">
            {statusHistory.map((item, index) => (
              <div key={index} className="historyItem">
                <div className="historyTimeStamp">{item.timestamp}</div>
                <div className={`historyDot ${index === 0 ? 'active' : ''}`} />
                <div className="historyContent">
                  <p className="historyStatus">{item.status}</p>
                  <p className="historyDescription">{item.description}</p>
                  {item.trackingId && <p className="historyTracking">Tracking ID: {item.trackingId}</p>}
                  {item.trackingLink && <p className="historyTracking">Tracking Link: <a href={item.trackingLink} target="_blank" rel="noopener noreferrer">{item.trackingLink}</a></p>}
                  {index === 0 && !isSeller && (item.status === 'Delivered' || item.status === 'Picked Up') && (
                    <div className="confirmationButtons">
                      <p style={{ margin: "8px 0px" }}>{item.status === 'Delivered' ? 'Have you received your item?' : 'Have you picked up your item?'}</p>
                      <p style={{ margin: "8px 0px", fontSize: "13px", color: "#555", lineHeight: 1.45 }}>
                        If you take no action, this order will be marked as completed automatically after 3 days.
                        {order?.buyerConfirmationDueAt && (
                          <span>{' '}Respond by{' '}
                            <strong>{new Date(order.buyerConfirmationDueAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</strong>.</span>
                        )}
                      </p>
                      <button className='modalButton submit' onClick={handleAccept} disabled={isUpdating}>
                        {isUpdating ? <Loader type="miniPlus" /> : 'Yes'}
                      </button>
                      <button className='modalButton cancel' onClick={handleReject} disabled={isUpdating} style={{ marginLeft: "10px" }}>
                        {isUpdating ? <Loader type="miniPlus" /> : 'No'}
                      </button>
                    </div>
                  )}
                  {index === 0 && (item.status === 'OnHold') && ((isSeller && item.cancellationRequestedBy === 'buyer') || (!isSeller && item.cancellationRequestedBy === 'seller')) && (
                    <div className="confirmationButtons" style={{ marginTop: "10px" }}>
                      <button className='modalButton submit' onClick={handleAcceptCancellation} disabled={isUpdating}>
                        {isUpdating ? <Loader type="miniPlus" /> : 'Accept'}
                      </button>
                      <button className='modalButton cancel' onClick={handleRejectCancellation} disabled={isUpdating} style={{ marginLeft: "10px" }}>
                        {isUpdating ? <Loader type="miniPlus" /> : 'Reject'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {modalOpen && (
        <div className="modalOverlay" onClick={() => setModalOpen(false)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <p>{modalMessage}</p>
            <button className='modalButton submit' onClick={() => setModalOpen(false)}>OK</button>
          </div>
        </div>
      )}
      {modal.open && (
        <div className="modalOverlay" onClick={() => setModal({ ...modal, open: false })}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <p>{modal.message}</p>
            {modal.type === 'message' ? (
              <button onClick={() => setModal({ ...modal, open: false })} className='modalButton submit'>OK</button>
            ) : (
              <div style={{ display: 'flex', gap: '10px', justifyContent: "center" }}>
                <button onClick={() => { modal.onConfirm(); setModal({ ...modal, open: false }); }} className='modalButton submit'>Yes</button>
                <button onClick={() => setModal({ ...modal, open: false })} className='modalButton cancel'>No</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;